import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth, db } from '@/api/firebaseClient';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  updateProfile
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [appPublicSettings, setAppPublicSettings] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Get user role and additional data from Firestore
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          let userData = userDoc.data();
          
          // If user document doesn't exist, create it with default role
          if (!userDoc.exists()) {
            userData = {
              email: firebaseUser.email,
              displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0],
              role: 'user', // Default role
              created_date: new Date().toISOString(),
            };
            await setDoc(userDocRef, userData);
          }

          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName || userData?.displayName || firebaseUser.email,
            role: userData?.role || 'user',
            photoURL: firebaseUser.photoURL,
          });
          setIsAuthenticated(true);
          setAuthError(null);
        } catch (error) {
          console.error('Error fetching user data:', error);
          // Still set user but without role data
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName || firebaseUser.email,
            role: 'user',
            photoURL: firebaseUser.photoURL,
          });
          setIsAuthenticated(true);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
      setIsLoadingAuth(false);
    });

    return () => unsubscribe();
  }, []);

  /**
   * Login with email and password
   */
  const loginWithEmail = async (email, password) => {
    try {
      setIsLoadingAuth(true);
      setAuthError(null);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Login error:', error);
      setAuthError({
        type: 'login_failed',
        message: getAuthErrorMessage(error.code),
      });
      throw error;
    } finally {
      setIsLoadingAuth(false);
    }
  };

  /**
   * Login with Google
   */
  const loginWithGoogle = async () => {
    try {
      setIsLoadingAuth(true);
      setAuthError(null);
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error('Google login error:', error);
      setAuthError({
        type: 'login_failed',
        message: getAuthErrorMessage(error.code),
      });
      throw error;
    } finally {
      setIsLoadingAuth(false);
    }
  };

  /**
   * Register new user
   */
  const register = async (email, password, displayName) => {
    try {
      setIsLoadingAuth(true);
      setAuthError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      if (displayName) {
        await updateProfile(userCredential.user, { displayName });
      }
      
      // Create user document in Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email,
        displayName: displayName || email.split('@')[0],
        role: 'user',
        created_date: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Registration error:', error);
      setAuthError({
        type: 'registration_failed',
        message: getAuthErrorMessage(error.code),
      });
      throw error;
    } finally {
      setIsLoadingAuth(false);
    }
  };

  /**
   * Logout
   */
  const logout = async (shouldRedirect = true) => {
    try {
      await signOut(auth);
      setUser(null);
      setIsAuthenticated(false);
      if (shouldRedirect) {
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  /**
   * Send password reset email
   */
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  /**
   * Navigate to login (compatibility with Base44)
   */
  const navigateToLogin = () => {
    window.location.href = '/login';
  };

  /**
   * Check app state (compatibility with Base44)
   */
  const checkAppState = async () => {
    // In Firebase, auth state is handled by onAuthStateChanged
    return;
  };

  /**
   * Check if user is admin
   */
  const isAdmin = () => {
    return user?.role === 'admin';
  };

  /**
   * Get user-friendly error messages
   */
  const getAuthErrorMessage = (errorCode) => {
    const messages = {
      'auth/user-not-found': 'No existe una cuenta con este email',
      'auth/wrong-password': 'Contraseña incorrecta',
      'auth/email-already-in-use': 'Este email ya está registrado',
      'auth/weak-password': 'La contraseña debe tener al menos 6 caracteres',
      'auth/invalid-email': 'Email inválido',
      'auth/too-many-requests': 'Demasiados intentos. Intenta más tarde',
      'auth/popup-closed-by-user': 'Inicio de sesión cancelado',
      'auth/invalid-credential': 'Credenciales inválidas',
    };
    return messages[errorCode] || 'Error de autenticación';
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      isLoadingAuth,
      isLoadingPublicSettings,
      authError,
      appPublicSettings,
      logout,
      loginWithEmail,
      loginWithGoogle,
      register,
      resetPassword,
      navigateToLogin,
      checkAppState,
      isAdmin,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
